// src/components/GrantConsentModal.jsx
import { useState, useEffect } from 'react';
import { X, AlertCircle, CheckCircle } from 'lucide-react';
import { consentService } from '../services/consentService';
import LoadingSpinner from './LoadingSpinner';
import Modal from './Modal';
import styles from './GrantConsentModal.module.css';

const AVAILABLE_SCOPES = [
  { id: 'availability:read:freebusy', label: 'View Free/Busy Times', description: 'Allow viewing when you are free or busy (no event details)' },
  { id: 'availability:read:details', label: 'View Calendar Details', description: 'Allow viewing full calendar event details (titles, descriptions)' },
  { id: 'booking:propose', label: 'Propose Appointments', description: 'Allow proposing appointment times' }
];

export default function GrantConsentModal({ onClose, onSuccess }) {
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [selectedBusiness, setSelectedBusiness] = useState('');
  const [selectedScopes, setSelectedScopes] = useState(['availability:read:freebusy', 'booking:propose']);
  const [expiresInDays, setExpiresInDays] = useState(30);
  const [neverExpires, setNeverExpires] = useState(false);

  useEffect(() => { loadBusinesses(); }, []);

  const loadBusinesses = async () => {
    try {
      setLoading(true); setError(null);
      const { data, error: fetchError } = await consentService.getBusinessesForConsent();
      if (fetchError) throw fetchError;
      console.log('Loaded businesses:', data);
      setBusinesses(data || []);
    } catch (err) {
      console.error('Error loading businesses:', err);
      setError(err.message || 'Failed to load businesses');
    } finally {
      setLoading(false);
    }
  };

  const handleScopeToggle = (scopeId) => {
    setSelectedScopes(prev =>
      prev.includes(scopeId) ? prev.filter(s => s !== scopeId) : [...prev, scopeId]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedBusiness) { setError('Please select a business'); return; }
    if (selectedScopes.length === 0) { setError('Please select at least one permission'); return; }
    try {
      setSubmitting(true); setError(null);
      let expiresAt = null;
      if (!neverExpires) {
        expiresAt = new Date();
        expiresAt.setDate(expiresAt.getDate() + expiresInDays);
      }
      const { data, error: createError } = await consentService.createConsent(selectedBusiness, selectedScopes, expiresAt);
      if (createError) throw createError;
      const { data: fullConsent } = await consentService.getConsent(data.id);
      onSuccess(fullConsent || data);
    } catch (err) {
      console.error('Error creating consent:', err);
      setError(err.message || 'Failed to grant access');
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={true} onClose={onClose} title="Grant Access" size="sm">
      <div className={styles.header}>
        <h2 className={styles.headerTitle}>Grant Access</h2>
        <button onClick={onClose} className={styles.closeBtn} aria-label="Close"><X size={24} /></button>
      </div>

      <form onSubmit={handleSubmit} className={styles.form}>
        {error && (
          <div className={styles.errorBox}>
            <AlertCircle size={20} className={styles.errorIcon} aria-hidden="true" />
            <div>
              <p className={styles.errorTitle}>Error</p>
              <p className={styles.errorMsg}>{error}</p>
            </div>
          </div>
        )}

        <div className={styles.field}>
          <label className={styles.label}>Select Business</label>
          {loading ? (
            <div className={styles.loadingWrap}><LoadingSpinner size="sm" text="Loading businesses..." /></div>
          ) : businesses.length === 0 ? (
            <p className={styles.emptyText}>No businesses available. Businesses need to create profiles first.</p>
          ) : (
            <select value={selectedBusiness} onChange={e => setSelectedBusiness(e.target.value)} className={styles.select} required>
              <option value="">Choose a business...</option>
              {businesses.map(b => (
                <option key={b.user_id} value={b.user_id}>{b.business_name} - {b.service_type}</option>
              ))}
            </select>
          )}
          <p className={styles.hint}>Select which business can access your availability</p>
        </div>

        <div className={styles.field}>
          <label className={styles.label}>Permissions</label>
          <div className={styles.scopeList}>
            {AVAILABLE_SCOPES.map(scope => (
              <label
                key={scope.id}
                className={`${styles.scopeItem}${selectedScopes.includes(scope.id) ? ` ${styles.scopeItemChecked}` : ''}`}
              >
                <input
                  type="checkbox"
                  checked={selectedScopes.includes(scope.id)}
                  onChange={() => handleScopeToggle(scope.id)}
                  className={styles.checkbox}
                />
                <div className={styles.scopeContent}>
                  <div className={styles.scopeLabel}>
                    <span>{scope.label}</span>
                    {selectedScopes.includes(scope.id) && (
                      <CheckCircle size={14} className={styles.scopeCheck} aria-hidden="true" />
                    )}
                  </div>
                  <p className={styles.scopeDesc}>{scope.description}</p>
                </div>
              </label>
            ))}
          </div>
          <p className={styles.hint}>Select the permissions this business will have</p>
        </div>

        <div className={styles.field}>
          <label className={styles.label}>Expiration</label>
          <div className={styles.expiryList}>
            <label className={styles.checkboxRow}>
              <input type="checkbox" checked={neverExpires} onChange={e => setNeverExpires(e.target.checked)} className={styles.checkbox} />
              <span className={styles.checkboxLabel}>Never expires</span>
            </label>
            {!neverExpires && (
              <div>
                <label className={styles.subLabel}>Expires in (days)</label>
                <input
                  type="number" min="1" max="365"
                  value={expiresInDays}
                  onChange={e => setExpiresInDays(parseInt(e.target.value))}
                  className={styles.numInput}
                />
                <p className={styles.hint}>
                  Access will expire on{' '}
                  {new Date(Date.now() + expiresInDays * 24 * 60 * 60 * 1000).toLocaleDateString('en-US', {
                    year: 'numeric', month: 'long', day: 'numeric'
                  })}
                </p>
              </div>
            )}
          </div>
        </div>

        <div className={styles.footer}>
          <button type="button" onClick={onClose} disabled={submitting} className={styles.btnCancel}>Cancel</button>
          <button type="submit" disabled={submitting || loading || businesses.length === 0} className="btn-primary">
            {submitting ? 'Granting Access...' : 'Grant Access'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
